/**
 * Load-time node dimension backstop.
 *
 * ## What went wrong
 *
 * A node with no `width` is unbounded. `src/styles/components/_nodes.css` gives
 * the generator and container node types `width: 100%; max-width: none`, and a
 * React Flow node element is absolutely positioned inside a full-canvas
 * container — so `100%` resolves to the CANVAS width, and `height: 100%` of an
 * auto-height parent collapses to the content height. React Flow measures that,
 * autosave persists it, and the first viewer's screen width is baked into the
 * flow forever. A staging flow created through the MCP came back with five
 * nodes at `width: 2560` (a 1440p screen) rendering ~30px tall.
 *
 * `geminiOmni` in that same flow was 392 rather than 2560 — not because its
 * content is compact, but because `geminiOmni` appears NOWHERE in `_nodes.css`,
 * so the `width: 100%` rule never applied to it and it measured at its natural
 * content width.
 *
 * The root cause is fixed on the write side (`server/config/nodeDimensions.ts`
 * + `validateGraph`). This module is the second layer: it repairs what is
 * already stored, on the way in, so a flow self-corrects the next time somebody
 * opens and saves it. There is deliberately NO data migration.
 *
 * ## The rule, stated so it can be argued with
 *
 * For each node whose type resolves in `NODE_REGISTRY`:
 *
 *   1. `width` (or `height`) missing, zero, negative or not a finite number
 *      → set it to that type's registry default.
 *   2. `width` present and >= `max(3 x defaultWidth, 1200)`
 *      → the stored size is a screen measurement, not a choice: reset BOTH
 *        width and height to the registry defaults.
 *   3. Anything else is left exactly as it was.
 *
 * A repaired node also has `measured` dropped, so React Flow re-measures
 * against the restored box instead of re-applying the stale one.
 *
 * ### Why those two thresholds and not one
 *
 * Users legitimately resize nodes, so the default alone cannot be the rule. The
 * multiple (3x) is what makes this proportional to the node — an Output node's
 * default is 480, a Kling node's is 448, and "three times its own size" means
 * the same thing for both. The absolute floor (1200px) is what stops the
 * multiple from biting small-default nodes: without it `inpaint` (360) would be
 * repaired at 1080 and `imagePanel` (420) at 1260, and 1080px is a perfectly
 * ordinary width to drag an image container to.
 *
 * So the effective thresholds are 1344 for a default node, 1440 for an Output,
 * 1260 for an ImagePanel, 1200 for an Inpaint. Below its threshold a node is
 * NEVER touched: a deliberately widened 900px or 1100px node survives every
 * reload unchanged.
 *
 * ### What this rule knowingly gets wrong
 *
 * - A user who genuinely drags a node past its threshold loses that resize on
 *   the next load and has to drag it again. Annoying, recoverable, rare.
 * - A flow first opened in a browser window narrower than the threshold (say a
 *   1280px canvas, under a default node's 1344) keeps its bad width. It renders
 *   too wide but not grotesquely, and tightening the floor to catch it would
 *   start eating real resizes. The over-repair failure is a lost drag; the
 *   under-repair failure is a flow that stays broken. Both are bounded, and
 *   this is where the line was drawn.
 *
 * A narrower signature was considered and rejected: "wide AND height collapsed
 * below the default" discriminates better in the observed case, but it silently
 * misses a corrupt node whose content happened to be tall — and a backstop that
 * misses is the failure this module exists to end.
 */

import { getNodeDimensions, getNodeByReactFlowType } from '../config/nodeRegistry';

/** A stored width at or above `multiple x default` is a measurement, not a choice. */
export const ABSURD_WIDTH_MULTIPLE = 3;

/**
 * ...but never below this many pixels, whatever the node's default is. Keeps
 * small-default node types (inpaint 360, imagePanel 420) out of the repair for
 * widths a person would plausibly have chosen.
 */
export const ABSURD_WIDTH_FLOOR = 1200;

/** The narrowest stored width this module will treat as corrupt for a type. */
export function absurdWidthThreshold(defaultWidth: number): number {
  return Math.max(defaultWidth * ABSURD_WIDTH_MULTIPLE, ABSURD_WIDTH_FLOOR);
}

/** Structural shape of a persisted React Flow node, minus everything unused. */
interface SizedNode {
  type?: string;
  width?: number | null;
  height?: number | null;
  measured?: { width?: number; height?: number } | null;
}

function isUsableDimension(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Apply the rule above to one node. Returns the SAME object reference when
 * nothing needs changing, so an untouched flow does not churn React Flow's
 * identity checks (and so a test can assert "unchanged" by reference).
 */
export function repairNodeDimensions<T extends SizedNode>(node: T): T {
  if (!node || typeof node !== 'object') return node;

  // An unknown type has no registry default to apply. Leave it alone; the
  // validator, not the layout, is the place that complains about it.
  const type = typeof node.type === 'string' ? node.type : '';
  if (!type || !getNodeByReactFlowType(type)) return node;

  const defaults = getNodeDimensions(type);
  const hasWidth = isUsableDimension(node.width);
  const hasHeight = isUsableDimension(node.height);

  // Rule 2 — a baked-in screen measurement. Both axes are bogus, so both reset.
  if (hasWidth && (node.width as number) >= absurdWidthThreshold(defaults.width)) {
    return { ...node, width: defaults.width, height: defaults.height, measured: undefined };
  }

  // Rule 1 — never sized in the first place.
  if (!hasWidth || !hasHeight) {
    return {
      ...node,
      width: hasWidth ? node.width : defaults.width,
      height: hasHeight ? node.height : defaults.height,
      // A stale `measured` from a previous unbounded render would otherwise be
      // what the first paint uses.
      measured: undefined,
    };
  }

  // Rule 3 — a size the user chose. Do not touch it.
  return node;
}

/**
 * Apply {@link repairNodeDimensions} across a loaded flow's nodes.
 *
 * Call this AFTER legacy node-type migration, so `output` has already become
 * `customOutput` and resolves to the right default.
 */
export function repairNodeDimensionsAll<T extends SizedNode>(nodes: T[]): T[] {
  if (!Array.isArray(nodes)) return nodes;
  return nodes.map(repairNodeDimensions);
}
